import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Calendar, Clock, MapPin, ChevronLeft, ChevronRight, ArrowRight, Sparkles } from 'lucide-react'
import { useCollection } from '../../hooks/useFirestore'
import { events as defaultEvents } from '../../data/seedData'
import { formatDate, getCountdown, isFutureDate } from '../../utils/helpers'

const EventCard = ({ event, index }) => {
  const [countdown, setCountdown] = useState(getCountdown(event.date))
  // Parse date as local to avoid timezone issues
  const dateParts = event.date.split('-')
  const eventDate = new Date(dateParts[0], dateParts[1] - 1, dateParts[2])
  const day = eventDate.getDate()
  const month = eventDate.toLocaleString('default', { month: 'short' }).toUpperCase()

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(getCountdown(event.date))
    }, 1000)
    return () => clearInterval(timer)
  }, [event.date])

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      whileHover={{ y: -8 }}
      className="group flex-shrink-0 w-80 bg-white rounded-3xl overflow-hidden shadow-soft hover:shadow-soft-lg transition-all duration-500"
    >
      {/* Image */}
      <div className="relative h-48 overflow-hidden">
        <img
          src={event.imageUrl || 'https://images.unsplash.com/photo-1438232992991-995b7058bbb3?w=600'}
          alt={event.title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-dark/60 to-transparent" />

        {/* Countdown Badge */}
        {!countdown.expired && (
          <div className="absolute top-4 right-4 bg-gradient-to-r from-accent-500 to-accent-600 text-white px-3 py-1.5 rounded-full text-xs font-bold shadow-lg">
            {countdown.days > 0
              ? `${countdown.days}d ${countdown.hours}h`
              : `${countdown.hours}h ${countdown.minutes}m`}
          </div>
        )}

        {/* Date Badge */}
        <div className="absolute top-4 left-4 bg-white rounded-2xl p-3 text-center shadow-lg">
          <span className="block text-2xl font-heading font-bold text-dark-100">{day}</span>
          <span className="block text-xs font-semibold text-primary-600">{month}</span>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <h3 className="text-lg font-heading font-bold text-dark-100 mb-3 group-hover:text-primary-600 transition-colors line-clamp-2">
          {event.title}
        </h3>

        <div className="space-y-2 mb-4">
          <div className="flex items-center gap-2 text-dark-300 text-sm">
            <div className="p-1.5 rounded-lg bg-primary-50">
              <Clock className="w-3.5 h-3.5 text-primary-500" />
            </div>
            <span>{event.time}</span>
          </div>
          <div className="flex items-center gap-2 text-dark-300 text-sm">
            <div className="p-1.5 rounded-lg bg-secondary-50">
              <MapPin className="w-3.5 h-3.5 text-secondary-500" />
            </div>
            <span className="truncate">{event.location}</span>
          </div>
        </div>

        <Link
          to="/events"
          className="inline-flex items-center gap-2 text-primary-600 font-semibold text-sm hover:gap-3 transition-all"
        >
          Learn More
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </motion.div>
  )
}

const FeaturedEvents = () => {
  const scrollRef = useRef(null)
  const { data: events } = useCollection('events', {
    orderByField: 'date',
    orderDirection: 'asc',
  })

  const upcomingEvents = (events?.length > 0 ? events : defaultEvents)
    .filter(e => e.isPublished !== false && isFutureDate(e.date))
    .slice(0, 6)

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = direction === 'left' ? -340 : 340
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' })
    }
  }

  if (upcomingEvents.length === 0) return null

  return (
    <section className="py-24 bg-white relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-100/50 rounded-full blur-3xl" />
      <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-secondary-100/50 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary-100 text-secondary-700 text-sm font-semibold mb-4">
              <Calendar className="w-4 h-4" />
              What's Happening
            </span>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-dark-100">
              Upcoming <span className="gradient-text">Events</span>
            </h2>
          </motion.div>

          <div className="flex items-center gap-3">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => scroll('left')}
              className="p-3 bg-white rounded-2xl shadow-soft hover:shadow-soft-lg transition-all border border-gray-100"
            >
              <ChevronLeft className="w-5 h-5 text-dark-300" />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => scroll('right')}
              className="p-3 bg-white rounded-2xl shadow-soft hover:shadow-soft-lg transition-all border border-gray-100"
            >
              <ChevronRight className="w-5 h-5 text-dark-300" />
            </motion.button>
          </div>
        </div>

        {/* Events Carousel */}
        <div
          ref={scrollRef}
          className="flex gap-6 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory -mx-4 px-4"
        >
          {upcomingEvents.map((event, index) => (
            <div key={event.id} className="snap-start">
              <EventCard event={event} index={index} />
            </div>
          ))}
        </div>

        {/* View All Link */}
        <motion.div
          className="mt-12 text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <Link to="/events">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl bg-gradient-to-r from-secondary-500 to-secondary-600 text-white font-semibold shadow-lg shadow-secondary-500/25 hover:shadow-xl transition-all"
            >
              <Calendar className="w-5 h-5" />
              View All Events
              <ArrowRight className="w-5 h-5" />
            </motion.button>
          </Link>
        </motion.div>
      </div>
    </section>
  )
}

export default FeaturedEvents
